/**
 * Редактор состава тенант-культуры-смеси.
 *
 * Решение founder (2026-07-28): агрегаты и смеси живут у ТЕНАНТА, а не в
 * системном справочнике. «Многолетние травы (люц+эсп)» — имя тенант-культуры,
 * Люцерна 60% + Эспарцет 40% — её состав из системного справочника.
 *
 * Строки «культура + %»: добавить / удалить, поиск культуры такой же, как в
 * `CropMatchDialog` (GET /reference/crops?search=). Доли НЕ обязаны давать 100
 * — агроном вводит как знает; расхождение показываем предупреждением, а не
 * блокировкой (бэк ведёт себя так же).
 *
 * DS: только @/components/ui/* примитивы, semantic Tailwind, без hex.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Search, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { cropLabel } from '@/lib/cropLabel';
import { toast } from '@/lib/toast';
import { ApiError } from '@/lib/api';
import {
  useCanonSearch,
  useCropComponents,
  useSaveCropComponents,
  type CropQueueItem,
} from '@/lib/reference/tenantCrops';

/** Строка редактора: культура + доля как строка (поле ввода). */
interface Row {
  crop_id: string;
  crop_name: string;
  share: string;
}

function errMsg(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.detail : fallback;
}

/** «60» / «60.5» / '' → number | null. Мусор → null (бэк не блокирует состав). */
function parseShare(value: string): number | null {
  const raw = value.trim().replace(',', '.');
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function CropMixDialog({
  item,
  open,
  onOpenChange,
}: {
  item: CropQueueItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [query, setQuery] = useState('');
  const [dirty, setDirty] = useState(false);

  const components = useCropComponents(open ? (item?.id ?? null) : null);
  const search = useCanonSearch(query);
  const save = useSaveCropComponents();

  // Загруженный состав → строки редактора (только пока пользователь не правил,
  // иначе рефетч затирал бы ввод).
  useEffect(() => {
    if (!open) return;
    if (dirty) return;
    const loaded = components.data?.items;
    if (!loaded) return;
    setRows(
      loaded.map((c) => ({
        crop_id: c.crop_id,
        crop_name: c.crop_name_ru ?? 'Культура без названия',
        share: c.share_pct === null ? '' : String(c.share_pct),
      })),
    );
  }, [open, dirty, components.data]);

  function close() {
    setRows([]);
    setQuery('');
    setDirty(false);
    onOpenChange(false);
  }

  const total = useMemo(
    () =>
      rows.reduce((acc, r) => {
        const n = parseShare(r.share);
        return n === null ? acc : acc + n;
      }, 0),
    [rows],
  );
  const hasShares = rows.some((r) => parseShare(r.share) !== null);
  const shareOff = hasShares && Math.abs(total - 100) > 0.01;
  const badShare = rows.some((r) => {
    const n = parseShare(r.share);
    return n !== null && (n <= 0 || n > 100);
  });

  function addCrop(cropId: string, name: string) {
    if (rows.some((r) => r.crop_id === cropId)) {
      toast.info('Эта культура уже в составе');
      return;
    }
    setDirty(true);
    setRows((prev) => [...prev, { crop_id: cropId, crop_name: name, share: '' }]);
    setQuery('');
  }

  function removeCrop(cropId: string) {
    setDirty(true);
    setRows((prev) => prev.filter((r) => r.crop_id !== cropId));
  }

  function setShare(cropId: string, value: string) {
    setDirty(true);
    setRows((prev) =>
      prev.map((r) => (r.crop_id === cropId ? { ...r, share: value } : r)),
    );
  }

  function handleSave() {
    if (!item) return;
    save.mutate(
      {
        itemId: item.id,
        components: rows.map((r) => ({
          crop_id: r.crop_id,
          share_pct: parseShare(r.share),
        })),
      },
      {
        onSuccess: (res) => {
          if (res.warning) toast.info(res.warning);
          else if (rows.length === 0) toast.success('Состав очищен');
          else toast.success('Состав смеси сохранён');
          close();
        },
        onError: (e) => toast.error(errMsg(e, 'Не удалось сохранить состав')),
      },
    );
  }

  const results = search.data ?? [];

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : close())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Состав «{item?.local_name ?? '—'}»</DialogTitle>
          <DialogDescription>
            Из каких культур состоит эта смесь. Например: Люцерна 60%, Эспарцет
            40%. Доли можно не указывать — сохраним как есть.
          </DialogDescription>
        </DialogHeader>

        {/* Текущий состав */}
        {components.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : rows.length === 0 ? (
          <p className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
            Состав пуст. Найдите культуры ниже и добавьте их в смесь.
          </p>
        ) : (
          <ul className="space-y-2">
            {rows.map((r) => (
              <li
                key={r.crop_id}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-border px-3 py-2 sm:flex-nowrap"
              >
                <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                  {r.crop_name}
                </span>
                <div className="flex items-center gap-2">
                  <Label
                    htmlFor={`share-${r.crop_id}`}
                    className="sr-only"
                  >{`Доля культуры ${r.crop_name}, %`}</Label>
                  <Input
                    id={`share-${r.crop_id}`}
                    inputMode="decimal"
                    className="w-20"
                    placeholder="%"
                    value={r.share}
                    onChange={(e) => setShare(r.crop_id, e.target.value)}
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Убрать ${r.crop_name} из состава`}
                    onClick={() => removeCrop(r.crop_id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {rows.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Сумма долей: {Number(total.toFixed(2))}%
            {shareOff && ' — не 100%. Это допустимо, сохраним как есть.'}
          </p>
        )}
        {badShare && (
          <p className="text-xs text-destructive">
            Доля каждой культуры должна быть больше 0 и не больше 100%.
          </p>
        )}

        <Separator />

        {/* Добавление культуры */}
        <div className="space-y-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Добавить культуру в смесь…"
              className="pl-8"
            />
          </div>
          {query.trim().length >= 2 && (
            <div className="max-h-48 overflow-y-auto rounded-lg border border-border">
              {search.isLoading ? (
                <div className="space-y-2 p-3">
                  <Skeleton className="h-9 w-full" />
                  <Skeleton className="h-9 w-3/4" />
                </div>
              ) : search.isError ? (
                <p className="p-4 text-sm text-destructive">
                  Не удалось загрузить справочник.
                </p>
              ) : results.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground">
                  Ничего не найдено. Попробуйте другое название.
                </p>
              ) : (
                <ul>
                  {results.map((c) => (
                    <li
                      key={c.id}
                      className="flex items-center justify-between gap-3 border-b border-border px-3 py-2 last:border-b-0"
                    >
                      <span className="min-w-0 truncate font-medium text-foreground">
                        {cropLabel(c)}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => addCrop(c.id, cropLabel(c))}
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        Добавить
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={save.isPending}>
            Отмена
          </Button>
          <Button onClick={handleSave} disabled={save.isPending || badShare}>
            {save.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Сохранить состав
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CropMixDialog;
